import { memo } from 'radash';

const hexToRgb = (color: string): [number, number, number] | undefined => {
  const c = color.replace(/[^0-9a-fA-F]/g, '');
  switch (c.length) {
    // color with alpha channel
    case 8:
    case 6:
      return [parseInt(c.substring(0, 2), 16), parseInt(c.substring(2, 4), 16), parseInt(c.substring(4, 6), 16)];
    case 3:
      return [
        parseInt(c.substring(0, 1), 16) * 17,
        parseInt(c.substring(1, 2), 16) * 17,
        parseInt(c.substring(2, 3), 16) * 17,
      ];
    default:
      return undefined;
  }
};

export const colorIsLight = memo((color: string) => {
  const rgb = hexToRgb(color);
  if (!rgb) {
    return undefined;
  }
  const [r, g, b] = rgb;
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq > 128;
});

export function getTextColorBasedOnHexColor(
  hexcolor: string,
  lightColor: string = '#171717',
  darkColor: string = '#ffffff',
) {
  return colorIsLight(hexcolor) ? lightColor : darkColor;
}

// https://www.w3.org/TR/WCAG22/#dfn-relative-luminance
const relativeLuminance = ([r, g, b]: [number, number, number]) => {
  const [lr, lg, lb] = [r, g, b].map(channel => {
    const c = channel / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;
};

export type ContrastTextTheme = 'dark' | 'light';

/** Picks black or white text by the higher WCAG contrast ratio. An invalid color gives `dark`, to match a white fallback background. */
export const getContrastTextTheme = memo((color: string): ContrastTextTheme => {
  const rgb = hexToRgb(color);
  if (!rgb) {
    return 'dark';
  }
  const luminance = relativeLuminance(rgb);
  const contrastWithBlack = (luminance + 0.05) / 0.05;
  const contrastWithWhite = 1.05 / (luminance + 0.05);
  return contrastWithBlack >= contrastWithWhite ? 'dark' : 'light';
});
